import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { SongCard } from '../components/SongCard';
import { api } from '../lib/api';
import { GENRES } from '../data/mockData';

type SortKey = 'newest' | 'title' | 'genre';

export function LibraryPage() {
  const { songs, currentUser, deleteSong, requestWavePlacement, storageMb, limits } = useApp();
  const mySongs = songs.filter((s) => s.ownerId === currentUser?.id);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [error,setError] = useState('');
  const act = async (fn:()=>Promise<void>)=>{setError('');try{await fn();}catch(e:any){setError(e.message);}};
  const [query, setQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('All');
  const [privacyFilter, setPrivacyFilter] = useState('All');
  const [sort, setSort] = useState<SortKey>('newest');

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const filtered = useMemo(() => {
    let list = mySongs.filter((s) => {
      const matchQuery = !query || s.title.toLowerCase().includes(query.toLowerCase()) || s.genre.toLowerCase().includes(query.toLowerCase());
      const matchGenre = genreFilter === 'All' || s.genre === genreFilter;
      const matchPrivacy = privacyFilter === 'All' || s.privacy === privacyFilter;
      return matchQuery && matchGenre && matchPrivacy;
    });
    list = [...list].sort((a, b) => {
      if (sort === 'title') return a.title.localeCompare(b.title);
      if (sort === 'genre') return a.genre.localeCompare(b.genre);
      return b.createdAt.localeCompare(a.createdAt);
    });
    return list;
  }, [mySongs, query, genreFilter, privacyFilter, sort]);

  const play = async (song: typeof mySongs[number]) => {
    const url = api.audioUrl(song.fileUrl);
    if (playingId === song.id) { audioRef.current?.pause(); setPlayingId(null); return; }
    if (!audioRef.current) audioRef.current = new Audio();
    if (!url) return;
    audioRef.current.src = url; audioRef.current.onended=()=>setPlayingId(null);
    try {await audioRef.current.play();setPlayingId(song.id);}catch{setError('This audio could not be played. Please try again.');setPlayingId(null);}
  };

  const download = (song: typeof mySongs[number]) => {
    const url = api.audioUrl(song.fileUrl);
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = song.title || 'IAMMUZIK-track';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="grid">
      <div className="card">
        <div className="flex-between wrap gap-12">
          <div>
            <div className="section-title" style={{ margin: 0 }}>Your Library</div>
            <div className="muted" style={{ fontSize: 13 }}>Private by default until you choose to publish a clean version.</div>
          </div>
          <div className="flex wrap gap-8">
            <input className="input btn-sm" style={{ minWidth: 160 }} placeholder="Search title / genre…" value={query} onChange={(e) => setQuery(e.target.value)} />
            <select className="select btn-sm" value={genreFilter} onChange={(e) => setGenreFilter(e.target.value)}>
              <option value="All">All genres</option>
              {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select className="select btn-sm" value={privacyFilter} onChange={(e) => setPrivacyFilter(e.target.value)}>
              <option value="All">All status</option>
              <option value="PRIVATE">Private</option>
              <option value="PUBLIC_REQUESTED">Pending review</option>
              <option value="PUBLIC_APPROVED">On The Wave</option>
            </select>
            <select className="select btn-sm" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="newest">Newest</option>
              <option value="title">Title A–Z</option>
              <option value="genre">Genre</option>
            </select>
          </div>
        </div>
        {limits && (
          <div className="field-hint" style={{ marginTop: 12 }}>
            Storage: <strong>{storageMb} MB</strong> of {limits.storageMb} MB · {mySongs.length} of {limits.uploads} uploads
          </div>
        )}
      </div>

      {error && <p role="alert">{error}</p>}
      {filtered.length === 0 ? (
        <div className="empty">No songs match. Open Studio to upload a track.</div>
      ) : (
        <div className="grid cols-4">
          {filtered.map((song) => (
            <SongCard key={song.id} song={song}>
              <button className="btn btn-ghost btn-sm" disabled={!api.audioUrl(song.fileUrl)} onClick={() => play(song)}>{playingId === song.id ? '⏸ Pause' : '▶ Play'}</button>
              {song.privacy === 'PRIVATE' && song.contentRating === 'CLEAN' && (
                <button className="btn btn-gold btn-sm" onClick={() => act(()=>requestWavePlacement(song.id))}>Post to Wave</button>
              )}
              <button className="btn btn-outline btn-sm" disabled={!api.audioUrl(song.fileUrl)} onClick={() => download(song)}>⬇ Download</button>
              <button className="btn btn-danger btn-sm" onClick={() => act(()=>deleteSong(song.id))}>Archive</button>
            </SongCard>
          ))}
        </div>
      )}
    </div>
  );
}
